<template>
    <div class="flex flex-column justify-content-center align-items-center w-full h-full">
        <span class="H6-Medium py-4">
            {{ isEdit ? "Edit Notice" : "Create Notice" }}
        </span>

        <Form ref="formRef" @submit="handleSubmit" :initialValues="initialValues" class="form">
            <div class="grid w-full align-items-center justify-content-center">

                <div class="col-12">
                    <label for="title" class="Label align-self-start">Notice Title</label>
                    <InputText id="title" name="title" placeholder="Enter title here" size="small" variant="filled"
                        class="w-full" />
                </div>

                <div class="col-12 flex flex-column gap-3 pt-3">
                    <label for="description" class="Label align-self-start">Notice</label>
                    <Editor ref="editorRef" id="description" name="description" editorStyle="height: 280px" v-model="description" />
                </div>

                <div class="col-12 flex align-items-center gap-2 py-4">
                    <label class="Label">Publish</label>
                    <ToggleSwitch v-model="publish" />
                </div>

                <div class="col-12 flex gap-3">
                    <Button type="submit" :label="isEdit ? 'Update Notice' : 'Create Notice'"
                        class="teal-button-small" />
                </div>

            </div>
        </Form>
    </div>
</template>

<script setup>
import Button from "primevue/button";
import { Form } from "@primevue/forms";
import InputText from "primevue/inputtext";
import ToggleSwitch from "primevue/toggleswitch";
import { ref, computed, defineProps, defineEmits, watch, nextTick } from "vue";
import { notifySuccess, notifyError, notifyWarn } from "@/utils/notification";
import { createNote, updateNotice } from "@/api/tutor";
import Editor from "primevue/editor";

const props = defineProps({
    notice: { type: Object, default: null }
});

const emit = defineEmits(["refresh-notices"]);

const formRef = ref(null);
const editorRef = ref(null);
const publish = ref(false);
const description = ref("");


const initialValues = {
    title: "",
    description: ""
};

const isEdit = computed(() => !!props.notice);

async function handleSubmit(val) {
    try {
        const payload = { ...val.values, description: description.value, published: publish.value };

        if (isEdit.value) {
            await updateNotice(props.notice._id, payload);
            notifySuccess("Success", "Notice updated");
        } else {
            await createNote(payload);
            notifySuccess("Success", "Notice created");

            description.value = "";
            val.reset();
            publish.value = false;
        }

        emit("refresh-notices");
    } catch (error) {
        if (error.response?.data?.code === 403) {
            notifyWarn("Plan Limit Reached", error.response.data.message);
        } else {
            notifyError(error.response?.data?.status || "Error", error.response?.data?.message || "Failed to schedule class.");
        }
    }
}

watch(
    () => props.notice,
    async (data) => {
        if (data) {
            // Populate for Edit
            await nextTick();
            formRef.value?.setValues({
                title: data.title || "",
                description: data.description || ""
            });
            description.value = data.description || "";
            publish.value = data.published || false;
        } else {
            // Reset for Create
            await nextTick();
            formRef.value?.setValues({
                title: "",
                description: ""
            });
            description.value = "";
            publish.value = false;
        }
    },
    { immediate: true }
);
</script>

<style scoped>
.form {
    width: 100%;
}

.teal-button-small {
    padding: 8px 16px;
    height: 30px;
    font-size: 0.75rem;
}
</style>